import { useAtomValue } from 'jotai'
import { nanoid } from 'nanoid'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { compile } from '~/apps/ninesegsAnalyzer/routes/paths'
import { brandGroupIdAtom } from '~/atoms'
import { useKeyedAtomEntry } from '~/hooks/jotai'
import { useCreateSurveyFilterRestoringParams } from '~/hooks/useCreateSurveyFilterRestoringParams'
import { useSurveyQueryData } from '~/hooks/useSurveyQuery'
import { assertExists } from '~/utils'

import {
  columnsDiffsAtom,
  commentBySegmentDisplayTypeAtom,
  dataDisplayTypeAtom,
  hiddenRowIdsAtom,
  segmentDisplayTypeAtom,
  sortValueAtom,
  tabulatedBrandIdsAtom,
  tabulatedColumnCustomerAttributeIdsAtom,
  tabulatedCustomerAttributeIdsAtom,
  tabulatedQuestionIdsAtom,
} from '../../atoms'
import { pathKey } from '../../utils'

import { type RestoringParams } from './types'

export const useRestoringParams = () => {
  const { t } = useTranslation()
  const createSurveyFilterRestoringParams =
    useCreateSurveyFilterRestoringParams()

  const survey = useSurveyQueryData()

  const brandGroupId = useAtomValue(brandGroupIdAtom)

  const columnCustomerAttributeIds = useAtomValue(
    tabulatedColumnCustomerAttributeIdsAtom,
  )
  const brandIds = useAtomValue(tabulatedBrandIdsAtom)
  const questionIds = useAtomValue(tabulatedQuestionIdsAtom)
  const customerAttributeIds = useAtomValue(tabulatedCustomerAttributeIdsAtom)
  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)
  const segmentDisplayType = useAtomValue(segmentDisplayTypeAtom)

  const sortValue = useAtomValue(sortValueAtom)

  const hiddenRowIds = useAtomValue(hiddenRowIdsAtom)

  const [comment] = useKeyedAtomEntry(
    commentBySegmentDisplayTypeAtom,
    segmentDisplayType,
  )

  const columnsDiffs = useAtomValue(columnsDiffsAtom)

  assertExists(survey, t('assertion_failed'))
  assertExists(brandGroupId, t('assertion_failed'))

  return React.useMemo(() => {
    assertExists(survey, t('assertion_failed'))
    assertExists(brandGroupId, t('assertion_failed'))

    const hasComment = comment !== ''

    const params = createSurveyFilterRestoringParams<RestoringParams>(
      compile(pathKey, { surveyId: survey.id }),
      {
        version: 2,
        revision: 4,
        brandGroupId,
        columnCustomerAttributeIds,
        brandIds,
        questionIds,
        customerAttributeIds,
        dataDisplayType,
        segmentDisplayType,
        sortValue,
        hiddenRowIds,
        comment,
        columnCustomerAttributesDiffs: columnsDiffs,
      },
    )

    // Attach a unique commentRef only when this figure has a comment, so the saved
    // figure maps 1:1 to its DocumentImage. No comment → no marker, so comment-less
    // shares keep content-dedup.
    return hasComment ? { ...params, commentRef: nanoid() } : params
  }, [
    brandGroupId,
    brandIds,
    columnCustomerAttributeIds,
    columnsDiffs,
    comment,
    createSurveyFilterRestoringParams,
    customerAttributeIds,
    dataDisplayType,
    hiddenRowIds,
    questionIds,
    segmentDisplayType,
    sortValue,
    survey,
    t,
  ])
}
